import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, User, setPersistence, browserLocalPersistence } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { apiUrl } from '@/lib/api'

export interface AdminUser {
  id: string
  firebase_uid: string
  email: string
  display_name: string
  is_active: boolean
  last_login_at?: string | null
  created_at: string
  updated_at: string
}

export interface AdminRole { id: string; name: string; description?: string | null; is_system_role: boolean }
export interface AdminPermission { id: string; key: string; name: string; category: string; description?: string | null }

interface AdminAuthContextType {
  currentUser: User | null
  adminUser: AdminUser | null
  roles: AdminRole[]
  permissions: AdminPermission[]
  token: string | null
  loading: boolean
  operationLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (p: string) => boolean
  hasRole: (r: string) => boolean
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null)

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [permissions, setPermissions] = useState<AdminPermission[]>([])
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [operationLoading, setOperationLoading] = useState(false)

  const loadMe = useCallback(async (idToken: string) => {
    const res = await fetch(apiUrl('/api/admin/auth/me'), { headers: { Authorization: `Bearer ${idToken}` } })
    if (!res.ok) throw new Error('Failed to load admin context')
    const json = await res.json()
    setAdminUser(json.adminUser)
    setRoles(json.roles || [])
    setPermissions(json.permissions || [])
  }, [])

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(() => {})
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)
      if (user) {
        const idToken = await user.getIdToken()
        setToken(idToken)
        // Ensure backend recognizes the admin and returns roles/permissions
        try {
          // Optional: inform backend via /login for side effects (currently stateless)
          await fetch(apiUrl('/api/admin/auth/login'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken }) })
          await loadMe(idToken)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Admin bootstrap failed', e)
        }
      } else {
        setAdminUser(null)
        setRoles([])
        setPermissions([])
        setToken(null)
      }
      setLoading(false)
    })
    return () => unsub()
  }, [loadMe])

  const login = async (email: string, password: string) => {
    setOperationLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } finally {
      setOperationLoading(false)
    }
  }

  const logout = async () => {
    setOperationLoading(true)
    try {
      await fetch(apiUrl('/api/admin/auth/logout'), { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} })
      await signOut(auth)
      setAdminUser(null)
      setRoles([])
      setPermissions([])
      setToken(null)
    } finally {
      setOperationLoading(false)
    }
  }

  const hasPermission = useCallback((p: string) => permissions.some(x => x.key === p), [permissions])
  const hasRole = useCallback((r: string) => roles.some(x => x.name === r), [roles])

  const value: AdminAuthContextType = {
    currentUser,
    adminUser,
    roles,
    permissions,
    token,
    loading,
    operationLoading,
    login,
    logout,
    hasPermission,
    hasRole,
  }

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}


